import { Link } from "react-router-dom";
import { LEGAL_DOCS } from "@/lib/legal";
import { ArrowUpRight, Scales } from "@phosphor-icons/react";

/**
 * /legal — canonical index of every NaturePulse legal document.
 *
 * The Nav "Legal" tab and the site footer both link here. Each card
 * points to the full document (or, for anchor-style entries, to a
 * specific bookmark inside another doc).
 */
export default function LegalHub() {
  return (
    <div className="min-h-screen pt-28 pb-24 px-4 md:px-8 text-[color:var(--np-text)]">
      <div className="max-w-[1200px] mx-auto">

        {/* header */}
        <div className="flex items-start gap-3">
          <span className="mt-2 inline-grid place-items-center w-10 h-10 rounded-full bg-[color:var(--np-brand)]/10 text-[color:var(--np-brand)]">
            <Scales size={18} weight="bold" />
          </span>
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand)]">
              NaturePulse · Legal Notices
            </div>
            <h1 className="mt-2 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              Legal
            </h1>
            <p className="mt-4 max-w-2xl text-[color:var(--np-text-2)] leading-relaxed text-[15px]">
              Every policy that governs your use of NaturePulse and Farmers Pulse, in one place. Each document is
              anchor-linked so you can share or bookmark a specific clause. Written to be readable — not just
              enforceable.
            </p>
          </div>
        </div>

        {/* cards */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {LEGAL_DOCS.map((d, i) => {
            const external = d.kind === "external";
            const Wrapper = external ? "a" : Link;
            const wrapperProps = external
              ? { href: d.to, target: "_blank", rel: "noreferrer" }
              : { to: d.to };
            return (
              <Wrapper
                key={d.slug}
                {...wrapperProps}
                data-testid={`legal-card-${d.slug}`}
                className="group relative block rounded-2xl border border-black/10 hover:border-[color:var(--np-brand)] bg-white/60 hover:bg-white transition-all p-6 md:p-7"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-muted)]">
                      § {String(i + 1).padStart(2, "0")}
                    </div>
                    <h2 className="mt-1 font-serif-display text-2xl md:text-[26px] leading-tight tracking-tight text-[color:var(--np-text)] group-hover:text-[color:var(--np-brand)] transition-colors">
                      {d.label}
                    </h2>
                  </div>
                  <ArrowUpRight
                    size={18}
                    weight="bold"
                    className="shrink-0 text-[color:var(--np-muted)] group-hover:text-[color:var(--np-brand)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
                  />
                </div>
                <p className="mt-3 text-[14px] leading-relaxed text-[color:var(--np-text-2)]">
                  {d.description}
                </p>
                <div className="mt-5 flex items-center gap-3 font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-muted)]">
                  <span>Updated · {d.updated}</span>
                  {d.kind === "anchor" && <span>· Section bookmark</span>}
                  {external && <span>· External</span>}
                </div>
              </Wrapper>
            );
          })}
        </div>

        {/* contact strip */}
        <div className="mt-16 rounded-2xl border border-black/10 bg-white/60 p-6 md:p-8">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand)]">
            / Contact — Legal
          </div>
          <h3 className="mt-2 font-serif-display text-2xl md:text-3xl leading-tight tracking-tight">
            Questions about these policies?
          </h3>
          <p className="mt-3 text-[15px] text-[color:var(--np-text-2)] leading-relaxed max-w-xl">
            Reach the NaturePulse legal team directly. We usually reply within a few business days.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono-tech text-[11px] tracking-[0.22em] uppercase">
            <a
              className="text-[color:var(--np-brand)] hover:underline"
              href="mailto:legal@naturepulse.net"
              data-testid="legal-contact-email"
            >
              legal@naturepulse.net
            </a>
            <span className="text-[color:var(--np-muted)]">Social Pulse Inc. · 15166 SE Aston Loop · Portland, OR 97236 · USA</span>
          </div>
        </div>
      </div>
    </div>
  );
}
